import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useFormik } from 'formik';
import { useAuth, useFirestore } from 'reactfire';
import { doc, setDoc } from 'firebase/firestore';
import { AuthError, deleteUser } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import * as yup from 'yup';

import {
  TextField,
  Button,
  Typography,
  Link,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import { AuthContainer, AuthForm } from '../../styled/AuthPage.styled';

import { passwordRules } from '../../utils/passwordRules';
import { register, handleAuthError } from '../../utils/api/auth';

const registerSchema = yup.object().shape({
  email: yup.string().email('Неправильний email').required('Обов’язкове поле'),
  password: yup
    .string()
    .min(8, 'Пароль має бути не коротшим ніж 8 символів')
    .required('Обов’язкове поле')
    .matches(passwordRules, 'Пароль занадто простий'),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref('password')], 'Паролі не співпадають')
    .required('Обов’язкове поле'),
});

const RegisterPage = () => {
  const auth = useAuth();
  const firestore = useFirestore();
  const navigate = useNavigate();

  const [licenseAgreement, setLicenseAgreement] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      passwordConfirm: '',
    },
    validationSchema: registerSchema,
    async onSubmit({ email, password }, { setErrors }) {
      try {
        const { user } = await register(auth, email, password);

        const userRef = doc(firestore, 'users', user.uid);
        await setDoc(userRef, {
          role: 'user',
          email: user.email,
          uid: user.uid,
        });

        navigate('/dashboard');
      } catch (error) {
        if (error instanceof FirebaseError) {
          if (error.code === 'permission-denied') {
            await deleteUser(auth.currentUser!);
          }
        }

        const message = handleAuthError(error as AuthError);

        setErrors({ email: message });
      }
    },
  });

  return (
    <AuthContainer maxWidth="xs">
      <Typography variant="h5" marginBottom={3}>
        Реєстрація
      </Typography>
      <AuthForm onSubmit={formik.handleSubmit}>
        <TextField
          id="email"
          name="email"
          autoComplete="email"
          label="Email"
          autoFocus
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
        <TextField
          id="password"
          name="password"
          type="password"
          label="Пароль"
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
        />
        <TextField
          id="passwordConfirm"
          name="passwordConfirm"
          type="password"
          label="Підтвердження паролю"
          value={formik.values.passwordConfirm}
          onChange={formik.handleChange}
          error={
            formik.touched.passwordConfirm &&
            Boolean(formik.errors.passwordConfirm)
          }
          helperText={
            formik.touched.passwordConfirm && formik.errors.passwordConfirm
          }
        />
        <FormControlLabel
          checked={licenseAgreement}
          control={
            <Checkbox
              onChange={(event) => setLicenseAgreement(event.target.checked)}
            />
          }
          label={
            <Typography>
              Я погоджуюся з умовами&nbsp;
              <Link component={RouterLink} to="/license">
                публічної ліцензії
              </Link>
            </Typography>
          }
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!licenseAgreement || formik.isSubmitting}
        >
          Зареєструватися
        </Button>
        <Typography>
          Вже маєте аккаунт?&nbsp;
          <Link to="/login" component={RouterLink}>
            Увійти
          </Link>
        </Typography>
      </AuthForm>
    </AuthContainer>
  );
};

export default RegisterPage;
